import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { ShieldX, Lock } from 'lucide-react';
import { PermissionState } from '@/components/ui/PermissionState';
import { useLocation } from 'react-router-dom';
import { buildAuthHref } from '@/routing/authReturnTo';
import { useAdminCapability } from '@/hooks/useAdminCapability';

interface AdminProtectionProps {
  children: React.ReactNode;
}

export function AdminProtection({ children }: AdminProtectionProps) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { status: capabilityState } = useAdminCapability();
  const location = useLocation();
  const returnTo = `${location.pathname}${location.search}${location.hash}`;

  if (isLoading) {
    return (
      <PermissionState
        title="Verifying admin access..."
        busy
        mainAriaLabel="Admin access"
      />
    );
  }

  if (!isAuthenticated || !user) {
    return (
      <PermissionState
        title="Authentication Required"
        titleId="admin-access-title"
        icon={Lock}
        description="Please log in to access the admin dashboard."
        action={
          <a
            href={buildAuthHref('/auth', returnTo || '/admin')}
            className="inline-flex items-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Go to Login
          </a>
        }
      />
    );
  }

  if (capabilityState === 'idle' || capabilityState === 'loading') {
    return (
      <PermissionState
        title="Verifying admin capability..."
        busy
        mainAriaLabel="Admin access"
      />
    );
  }

  if (capabilityState !== 'allowed') {
    return (
      <PermissionState
        title="Access Denied"
        titleId="admin-access-denied-title"
        description={
          <>
            {capabilityState === 'unavailable'
              ? 'Admin access could not be verified.'
              : "You don't have permission to access the admin dashboard."}
            <br />
            <span className="mt-2 block text-sm text-gray-600">Logged in as: {user.email}</span>
          </>
        }
        action={
          <a
            href="/"
            className="inline-flex items-center rounded-md border border-transparent bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Return to Home
          </a>
        }
      />
    );
  }

  return <>{children}</>;
}
